import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';

export default function FogPlane() {
  const matRef = useRef();

  const uniforms = useMemo(
    () => ({
      time: { value: 0 },
      opacity: { value: 0.38 },
      zoom: { value: 2.2 },
      speed: { value: 1.35 },
    }),
    []
  );

  useFrame((state) => {
    const m = matRef.current;
    if (!m) return;

    m.uniforms.time.value = state.clock.elapsedTime;
  });

  return (
    <mesh>
      <planeGeometry args={[2, 2, 1, 1]} />
      <shaderMaterial
        ref={matRef}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        vertexShader={`
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = vec4(position, 1.0);
          }
        `}
        fragmentShader={`
          uniform float time;
          uniform float opacity;
          uniform float zoom;
          uniform float speed;
          varying vec2 vUv;

          float hash(vec2 p) {
            return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
          }

          float noise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);

            float a = hash(i);
            float b = hash(i + vec2(1.0, 0.0));
            float c = hash(i + vec2(0.0, 1.0));
            float d = hash(i + vec2(1.0, 1.0));

            vec2 u = f * f * (3.0 - 2.0 * f);
            return mix(a, b, u.x)
                + (c - a) * u.y * (1.0 - u.x)
                + (d - b) * u.x * u.y;
          }

          mat2 rot(float a) {
            float s = sin(a), c = cos(a);
            return mat2(c, -s, s, c);
          }

          void main() {
            float t = time * speed;

            vec2 uv = (vUv - 0.9) / zoom + 0.9;

            vec2 p = uv - 0.5;
            p *= rot(0.10 * sin(t * 0.35) + 0.08 * cos(t * 0.22));
            uv = p + 0.5;

            vec2 uvA = uv + vec2(t * 0.040, t * 0.018);
            vec2 uvB = uv + vec2(-t * 0.018, t * 0.030);

            float nA = 0.0;
            nA += 0.60 * noise(uvA * 3.0);
            nA += 0.28 * noise(uvA * 6.0);
            nA += 0.12 * noise(uvA * 12.0);

            float nB = 0.0;
            nB += 0.55 * noise(uvB * 4.0);
            nB += 0.30 * noise(uvB * 8.0);
            nB += 0.15 * noise(uvB * 16.0);

            float n = mix(nA, nB, 0.55);

            float fog = smoothstep(0.40, 0.72, n);
            fog = pow(fog, 0.75);
            fog *= 1.1;

            float bottom = smoothstep(1.0, 0.11, vUv.y);
            fog *= bottom;

            float edge =
              smoothstep(0.0, 0.25, vUv.x) *
              smoothstep(0.0, 0.25, vUv.y) *
              smoothstep(0.0, 0.25, 1.0 - vUv.x) *
              smoothstep(0.0, 0.25, 1.0 - vUv.y);

            fog *= edge;

            gl_FragColor = vec4(vec3(1.0), fog * opacity);
          }
        `}
      />
    </mesh>
  );
}
